import { rngFloat, type RngHolder } from '../math/rng';
import type { SimConfig } from '../config/simConfig';

// D46 Items 3+4 — difficulty as an AI-LAYER concept ONLY. The sim stays difficulty-free (§3: bot inputs
// are logged, replay never re-runs the AI), so NO ModeInfo/state/codec/protocol change. A level (1..5,
// default 3) rides the JSON room-config path → Room → BotDriver → a per-team DifficultyProfile threaded
// through TeamPlan.profile and consumed by actPlayer/actKeeper.
//
// OPPOSITE scaling (designer decision): pick the curve by human presence, NOT by which is "the AI".
//   • a team WITH a human, opponent all-bot, uses the ALLY curve   (L1 = brilliant support … L5 = clueless),
//   • a team WITHOUT a human (vs a human) uses the ENEMY curve      (L1 = near-dead        … L5 = brutal).
//   • PvP (humans on BOTH sides) ⇒ BOTH teams use the ENEMY curve — one global bot-IQ dial that scales UP
//     with the level (D85), so "easy = ALL bots dumb, hard = ALL bots smart", symmetric by construction.
// D85: LEVEL 3 IS THE EXACT CROSSOVER — allyProfile(3) === enemyProfile(3) === IDENTITY, bit-for-bit — so a
// human-vs-bots match at level 3 is EXACTLY equal between the teams (the user's calibration). The D56
// ladder (IDENTITY at 4) moved down one to pivot on 3; below 3 the old shape survives (enemies dumber,
// allies at-or-above baseline), above 3 likewise (enemies sharper, allies degraded).

export type DifficultyLevel = 1 | 2 | 3 | 4 | 5;
export type DifficultySide = 'ally' | 'enemy';

export interface DifficultyProfile {
  reactionMul: number; // × TACKLE_REACT_TICKS / TRAP_REACT_TICKS and the keeper reaction delay (higher = slower to react)
  aimNoiseMul: number; // D116: × the BASELINE aim error on bot kicks + the brain's shoot/dribble score noise (two-sided)
  tackleReachMul: number; // × the reach a BOT uses to DECIDE a tackle (never the sim's resolution)
  chaseSpeedCap: number; // ≤ 1 cap on a bot's move-input magnitude — dumber bots literally don't sprint
  passErrorMul: number; // D116: × the BASELINE pass mis-hit (aimElev lift) + the brain's pass-option score noise (two-sided)
  gkSkillMul: number; // D136 comment fix: keeper POSITIONING quality (shuffle cap ×, dive commit later) — NEVER the sim's gk.SKILL. (This line used to say reactionMul.)
  markingSlackMul: number; // × standoff/marking distances a bot keeps (lazy defending)
  // D136 — FALLIBILITY AS DESIGNED CHANNELS, not noise amplitude. Both scale `ai.WRONG_BALL_P` /
  // `ai.DUEL_MISS_P`; both are ≤ 0.02× at L5, so the top of the ladder barely errs.
  wrongBallP: number; // × the rate at which a carrier think takes the SECOND-best option
  duelMissP: number; // × the rate at which an armed bot tackle mistimes. D116's open contest-roll item, in the AI layer (PT4-P6).
}

export const DEFAULT_DIFFICULTY: DifficultyLevel = 3;
// D116: IDENTITY means "the baseline profile", NOT "no imperfection" — every multiplier is 1, and the
// error functions below now scale a nonzero baseline by those 1s. A level-3 bot mis-aims like a decent
// human; level 5 tightens the same error, level 1 blows it wide open.
export const IDENTITY_PROFILE: DifficultyProfile = Object.freeze({
  reactionMul: 1,
  aimNoiseMul: 1,
  tackleReachMul: 1,
  chaseSpeedCap: 1,
  passErrorMul: 1,
  gkSkillMul: 1,
  markingSlackMul: 1,
  wrongBallP: 1,
  duelMissP: 1,
});

// D116 — the ladder is TWO-SIDED. It used to inject error only ABOVE the baseline (`if (k <= 0) return 0`),
// and the enemy curve's L4/L5 multipliers are 0.8/0.6 — so levels 3, 4 AND 5 all carried exactly zero aim
// error and zero pass error, and raising the difficulty above the baseline bought no accuracy whatsoever.
// Now the BASELINE itself carries error (`cfg.ai.aimNoiseSigma`, `cfg.ai.passElevSigma` — both already §17
// knobs, the first of which had never been read by anything) and the multiplier scales it in both
// directions. IDENTITY is therefore no longer a no-op on the AI: a level-3 bot mis-hits, which is the
// point. The two curves still agree exactly AT level 3, which is what D85's crossover actually claims.

// Enemy curve — the AI opponent (and, in PvP, BOTH teams). L1 near-dead ("the goal is empty. Almost."),
// L5 brutal. As the level rises the enemy gets BETTER, so the "bad" multipliers fall toward (and past) 1.
// D85 re-pivot: IDENTITY moved 4 → 3 (the exact crossover). new L1 = old L1 (near-dead), new L2 = old L3
// (a notch below), new L3 = IDENTITY, new L4 = old L5 (sharp), new L5 = a fresh brutal+ top.
const ENEMY: Record<DifficultyLevel, DifficultyProfile> = {
  1: { reactionMul: 4, aimNoiseMul: 3, tackleReachMul: 0.5, chaseSpeedCap: 0.25, passErrorMul: 3, gkSkillMul: 0.25, markingSlackMul: 2.5, wrongBallP: 3, duelMissP: 3 },
  2: { reactionMul: 2.5, aimNoiseMul: 2, tackleReachMul: 0.75, chaseSpeedCap: 0.6, passErrorMul: 2, gkSkillMul: 0.6, markingSlackMul: 1.75, wrongBallP: 2, duelMissP: 2 },
  3: { ...IDENTITY_PROFILE },
  // D136 (PT4-P12): L4/L5 chaseSpeedCap rises ABOVE 1 to compensate `ai.BASE_MOVE_CAP` 0.9 — explicitly
  // flagged in the plan: L5 ends up faster than today's L5.
  4: { reactionMul: 0.8, aimNoiseMul: 0.8, tackleReachMul: 1.15, chaseSpeedCap: 1.0, passErrorMul: 0.8, gkSkillMul: 1.2, markingSlackMul: 0.9, wrongBallP: 0.25, duelMissP: 0.25 },
  5: { reactionMul: 0.6, aimNoiseMul: 0.6, tackleReachMul: 1.3, chaseSpeedCap: 1.1, passErrorMul: 0.6, gkSkillMul: 1.4, markingSlackMul: 0.75, wrongBallP: 0.16, duelMissP: 0.16 },
};

// Ally curve — the human's own bot team-mates (only when the opponent is all-bot). Mirrored: L1 brilliant
// support, L5 clueless. The D42 human-support doctrine (feed the human, obey calls) is NEVER disabled — a
// dumb ally still TRIES to serve you, he's just bad at it (aim noise + pass mis-hit do the work).
// D85 re-pivot: IDENTITY moved 4 → 3. new L1 = old L1 (brilliant), new L2 = old L3, new L3 = IDENTITY,
// new L4 = a fresh mild degrade, new L5 = old L5 (clueless).
const ALLY: Record<DifficultyLevel, DifficultyProfile> = {
  1: { reactionMul: 0.7, aimNoiseMul: 0.7, tackleReachMul: 1.1, chaseSpeedCap: 1, passErrorMul: 0.7, gkSkillMul: 1.1, markingSlackMul: 0.9, wrongBallP: 0.3, duelMissP: 0.3 },
  2: { reactionMul: 0.85, aimNoiseMul: 0.85, tackleReachMul: 1.05, chaseSpeedCap: 1, passErrorMul: 0.85, gkSkillMul: 1.05, markingSlackMul: 0.95, wrongBallP: 0.6, duelMissP: 0.6 },
  3: { ...IDENTITY_PROFILE },
  4: { reactionMul: 1.4, aimNoiseMul: 1.35, tackleReachMul: 0.9, chaseSpeedCap: 0.88, passErrorMul: 1.35, gkSkillMul: 0.88, markingSlackMul: 1.2, wrongBallP: 1.6, duelMissP: 1.6 },
  5: { reactionMul: 2, aimNoiseMul: 1.75, tackleReachMul: 0.8, chaseSpeedCap: 0.75, passErrorMul: 1.75, gkSkillMul: 0.75, markingSlackMul: 1.4, wrongBallP: 2.4, duelMissP: 2.4 },
};

/** Coerce any wire value to a valid 1..5 integer level (out-of-range / non-int ⇒ the default 3). */
export function clampDifficulty(v: unknown): DifficultyLevel {
  const n = typeof v === 'number' ? Math.round(v) : NaN;
  if (!Number.isFinite(n) || n < 1 || n > 5) return DEFAULT_DIFFICULTY;
  return n as DifficultyLevel;
}

export function difficultyProfile(level: DifficultyLevel, side: DifficultySide): DifficultyProfile {
  return { ...(side === 'enemy' ? ENEMY[level] : ALLY[level]) };
}

/** D85 curve selection. ALLY (the "help my team" curve) only when THIS team has a human and the OTHER is
 *  all-bot; ENEMY otherwise — a bot team vs a human, OR either team in a PvP lobby (humans on both sides,
 *  so raising the level makes ALL bots smarter, symmetric). */
export function pickSide(thisTeamHasHuman: boolean, otherTeamHasHuman: boolean): DifficultySide {
  return thisTeamHasHuman && !otherTeamHasHuman ? 'ally' : 'enemy';
}

/**
 * D116 · horizontal aim error (rad) on a bot kick — ± the baseline sigma, scaled by `aimNoiseMul`.
 *
 * This is the channel that decides a SHOT: `applyKicks` fires a shot along `req.aimYaw` untouched. On a
 * PASS it is (correctly) erased — the D34 soft-lock overwrites `aimYaw` with its own meeting point, and
 * that assist is bot-agnostic by design (§3: the sim never learns a bot is a bot). Pass error therefore
 * rides `passElevNoise` and the utility brain's decision noise instead, not this.
 */
export function aimNoise(prof: DifficultyProfile, rng: RngHolder, cfg: SimConfig): number {
  return (rngFloat(rng) * 2 - 1) * cfg.ai.aimNoiseSigma * prof.aimNoiseMul;
}

/**
 * D116 · the mis-hit PASS, in `PlayerInput.aimElev` units — ± the baseline sigma, scaled by `passErrorMul`.
 *
 * The soft-lock rewrites a pass's yaw and its power, but never its ELEVATION, so this is the only channel
 * through which a bot can actually strike a pass badly. The sim's `clamp01(aimElev)` means only the upward
 * half survives — a scuffed pass rises off the deck, a good one never digs into it — which is both the
 * realistic asymmetry and the reason this is a lift, not a jitter.
 *
 * (It replaces `jitterPassCharge`, which was provably inert: under D49 `AUTO_POWER` `applyKicks` discards
 * the request's `chargeTicks` entirely and recomputes it from the soft-lock target's true distance, so
 * nudging a bot's charge target changed nothing about the ball that left his boot.)
 */
export function passElevNoise(prof: DifficultyProfile, rng: RngHolder, cfg: SimConfig): number {
  return (rngFloat(rng) * 2 - 1) * cfg.ai.passElevSigma * prof.passErrorMul;
}
